import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send, RefreshCw, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";
import { SEO } from "@/components/SEO";
import { ART } from "@/lib/artwork";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";

export default function Contact() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { content } = useSiteContent();
  const isAr = i18n.language === "ar";
  const pageContent = content?.pages?.contact || {};
  const pageTitle = (isAr ? pageContent.title_ar : pageContent.title_en) || t("contact.title");
  const pageSubtitle = (isAr ? pageContent.subtitle_ar : pageContent.subtitle_en) || t("contact.subtitle");

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    phone: "",
    message: "",
    captcha_answer: "",
  });
  const [captcha, setCaptcha] = useState({ token: "", question: "" });
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const res = await client.get("/contact/captcha");
      setCaptcha({ token: res.data.token, question: res.data.question });
      setForm((f) => ({ ...f, captcha_answer: "" }));
    } catch (e) {
      console.error("Contact captcha failed:", e);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const canSubmit =
    form.full_name.trim().length >= 2 &&
    /.+@.+\..+/.test(form.email) &&
    form.phone.trim().length >= 4 &&
    form.message.trim().length >= 5 &&
    form.captcha_answer.trim().length >= 1 &&
    captcha.token;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await client.post("/contact", {
        ...form,
        captcha_token: captcha.token,
      });
      setDone(true);
      trackEvent(ANALYTICS_EVENTS.CONTACT_SUBMIT);
      toast.success(t("contact.success"));
    } catch (err) {
      console.error("Contact submit failed:", err);
      const detail = err?.response?.data?.detail;
      const isCaptcha = typeof detail === "string" && detail.toLowerCase().includes("captcha");
      toast.error(isCaptcha ? t("contact.captchaFailed") : detail || t("common.error"));
      if (isCaptcha) loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title={pageTitle} description={pageSubtitle} path="/contact" />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="contact-page">
      <div className="absolute inset-x-0 top-0 h-[360px] -z-10 overflow-hidden" aria-hidden>
        <img src={ART.soft} alt="" className="h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/75 to-background" />
      </div>
      <div className="text-center mb-10">
        <span className="section-eyebrow justify-center">Get in touch</span>
        <h1 className="font-display mt-4 text-4xl sm:text-5xl font-semibold tracking-tight">{pageTitle}</h1>
        <p className="mt-4 max-w-xl mx-auto text-muted-foreground leading-relaxed">{pageSubtitle}</p>
      </div>

      <Card className="rounded-3xl border border-border p-6 sm:p-10 shadow-[var(--shadow-card)]">
        {done ? (
          <div className="flex flex-col items-center text-center py-10" data-testid="contact-success">
            <div className="h-14 w-14 rounded-full bg-emerald-500/12 text-emerald-600 dark:text-emerald-400 grid place-items-center">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h2 className="font-display text-2xl font-semibold mt-5">{t("contact.success")}</h2>
            <Button variant="outline" className="mt-8 rounded-xl gap-2" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 rtl-flip" /> {t("common.back")}
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ct-name">{t("contact.fullName")}</Label>
                <Input
                  id="ct-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  className="mt-1.5 rounded-xl h-11"
                  required
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="ct-email">{t("contact.email")}</Label>
                <Input
                  id="ct-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-1.5 rounded-xl h-11"
                  required
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ct-org">{t("contact.organization")}</Label>
                <Input
                  id="ct-org"
                  value={form.organization}
                  onChange={(e) => update("organization", e.target.value)}
                  className="mt-1.5 rounded-xl h-11"
                  data-testid="contact-org-input"
                />
              </div>
              <div>
                <Label htmlFor="ct-phone">{t("contact.phone")}</Label>
                <Input
                  id="ct-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-1.5 rounded-xl h-11"
                  required
                  data-testid="contact-phone-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ct-msg">{t("contact.message")}</Label>
              <Textarea
                id="ct-msg"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="mt-1.5 rounded-xl min-h-32"
                required
                data-testid="contact-message-input"
              />
            </div>

            <div className="rounded-2xl border border-dashed border-border p-4 bg-muted/40">
              <Label className="block mb-2 text-xs uppercase tracking-widest text-muted-foreground">{t("contact.captcha")}</Label>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">{t("contact.captchaLabel")}</span>
                  <span className="font-display text-2xl font-semibold text-foreground tracking-wider" data-testid="contact-captcha-question" dir="ltr">
                    {captchaLoading ? "…" : captcha.question}
                  </span>
                  <span className="text-muted-foreground">=</span>
                </div>
                <Input
                  value={form.captcha_answer}
                  onChange={(e) => update("captcha_answer", e.target.value)}
                  className="rounded-xl h-11 w-32"
                  placeholder={t("contact.captchaPlaceholder")}
                  required
                  data-testid="contact-captcha-input"
                  inputMode="numeric"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={loadCaptcha}
                  disabled={captchaLoading}
                  className="rounded-xl gap-1.5 text-muted-foreground hover:text-foreground"
                  data-testid="contact-captcha-refresh"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${captchaLoading ? "animate-spin" : ""}`} />
                  {t("contact.captchaRefresh")}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={!canSubmit || submitting}
              className="w-full btn-brand rounded-xl h-12 text-base gap-2"
              data-testid="contact-submit-button"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Send className="h-4 w-4 rtl-flip" /> {t("contact.submit")}</>)}
            </Button>
          </form>
        )}
      </Card>
    </div>
    </>
  );
}
